import { useTranslation } from 'react-i18next';
import { Check, FileText, Pencil, Sparkles, Icon, type LucideIcon } from '../../../ui/Icon';
import type { StartMode } from '../types';

interface Props {
  value: StartMode | null;
  onChoose: (mode: StartMode) => void;
}

const OPTIONS: { mode: StartMode; icon: LucideIcon }[] = [
  { mode: 'ai', icon: Sparkles },
  { mode: 'documents', icon: FileText },
  { mode: 'empty', icon: Pencil },
];

export default function StartStep({ value, onChoose }: Props) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3">
      {OPTIONS.map(({ mode, icon }) => {
        const active = value === mode;
        return (
          <button
            key={mode} type="button" onClick={() => onChoose(mode)}
            aria-pressed={active}
            className={`group flex items-start gap-4 text-left rounded-2xl border p-4 sm:p-5 transition-all cursor-pointer ${
              active
                ? 'border-teal-500 bg-teal-50/70 dark:bg-teal-900/20 ring-1 ring-teal-500'
                : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 hover:border-teal-300 hover:shadow-sm'
            }`}
          >
            <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
              active ? 'bg-teal-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 group-hover:text-teal-600'
            }`}>
              <Icon as={icon} size={20} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2 text-[15px] font-semibold text-slate-900 dark:text-slate-100">
                {t(`dashboard.newTripWizard.start.${mode}.title`)}
                {mode === 'ai' && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300">
                    {t('dashboard.newTripWizard.start.recommended')}
                  </span>
                )}
              </span>
              <span className="block text-[13px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                {t(`dashboard.newTripWizard.start.${mode}.description`)}
              </span>
            </span>
            {active && <Icon as={Check} size={18} className="text-teal-600 shrink-0 mt-1" />}
          </button>
        );
      })}
    </div>
  );
}
